import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiMail, FiUser, FiLock, FiCheck, FiX, FiEye, FiEyeOff } from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import { toast } from 'react-toastify';
import '../auth/Auth.css';

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyInvitation();
    } else {
      setLoading(false);
      toast.error('Invalid invitation link');
    }
  }, [token]);

  const verifyInvitation = async () => {
    try {
      const response = await AxiosInstance.get(`${BASE_URL}/invitee/verify/${token}`);
      if (response.status === 200) {
        setInvitationData(response.data.data);
        setFormData({
          firstName: response.data.data.firstName || '',
          lastName: response.data.data.lastName || '',
          password: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid or expired invitation';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.password || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    try {
      setSubmitting(true);
      const response = await AxiosInstance.post(`${BASE_URL}/invitee/accept`, {
        token: token,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });
      if (response.status === 201) {
        toast.success('Account created successfully! You can now log in.');
        navigate('/auth');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to accept invitation';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this invitation?')) {
      return;
    }
    try {
      setSubmitting(true);
      await AxiosInstance.post(`${BASE_URL}/invitee/decline`, { token: token });
      toast.success('Invitation declined');
      navigate('/auth');
    } catch (error) {
      toast.error('Failed to decline invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <div className="auth-background-overlay"></div>
          <div className="auth-background-content">
            <h1>Welcome to MailMaster</h1>
            <p>Create stunning email campaigns that convert. Join thousands of businesses growing with our platform.</p>
            <div className="auth-features">
              <div className="feature">
                <div className="feature-icon">📧</div>
                <span>Drag & Drop Editor</span>
              </div>
              <div className="feature">
                <div className="feature-icon">📊</div>
                <span>Advanced Analytics</span>
              </div>
              <div className="feature">
                <div className="feature-icon">🎯</div>
                <span>Smart Automation</span>
              </div>
            </div>
          </div>
        </div>
        <div className="auth-form-container">
          <div className="auth-form-wrapper">
            <div className="auth-header">
              <h2>Verifying Invitation...</h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!invitationData) {
    return (
      <div className="auth-container">
        <div className="auth-background">
          <div className="auth-background-overlay"></div>
          <div className="auth-background-content">
            <h1>Welcome to MailMaster</h1>
            <p>Create stunning email campaigns that convert. Join thousands of businesses growing with our platform.</p>
            <div className="auth-features">
              <div className="feature">
                <div className="feature-icon">📧</div>
                <span>Drag & Drop Editor</span>
              </div>
              <div className="feature">
                <div className="feature-icon">📊</div>
                <span>Advanced Analytics</span>
              </div>
              <div className="feature">
                <div className="feature-icon">🎯</div>
                <span>Smart Automation</span>
              </div>
            </div>
          </div>
        </div>
        <div className="auth-form-container">
          <div className="auth-form-wrapper">
            <div className="auth-header">
              <h2>Invalid Invitation</h2>
              <p>This invitation link is invalid or has expired.</p>
            </div>
            <button onClick={() => navigate('/auth')} className="submit-btn" style={{width:'100%'}}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-background">
        <div className="auth-background-overlay"></div>
        <div className="auth-background-content">
          <h1>Welcome to MailMaster</h1>
          <p>Create stunning email campaigns that convert. Join thousands of businesses growing with our platform.</p>
          <div className="auth-features">
            <div className="feature">
              <div className="feature-icon">📧</div>
              <span>Drag & Drop Editor</span>
            </div>
            <div className="feature">
              <div className="feature-icon">📊</div>
              <span>Advanced Analytics</span>
            </div>
            <div className="feature">
              <div className="feature-icon">🎯</div>
              <span>Smart Automation</span>
            </div>
          </div>
        </div>
      </div>
      <div className="auth-form-container">
        <div className="auth-form-wrapper">
          <div className="auth-header">
            <h2>You're Invited!</h2>
            <p>
              <strong>{invitationData.inviterName}</strong> has invited you to join MailMaster as a
              <span className={`role-badge ${invitationData.role}`}> {invitationData.role.toUpperCase()}</span>
            </p>
          </div>
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <div className="input-wrapper">
                  <FiUser className="input-icon" />
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder="Enter your first name"
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <div className="input-wrapper">
                  <FiUser className="input-icon" />
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    placeholder="Enter your last name"
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <FiMail className="input-icon" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={invitationData.email}
                  disabled
                  className="disabled-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <FiLock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="password-input"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-wrapper">
                <FiLock className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  className="password-input"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>
            <div className="form-divider"></div>
            <div className="form-actions">
              <button
                type="button"
                onClick={handleDecline}
                className="decline-button"
                disabled={submitting}
              >
                <span className="icon"><FiX /></span>
                Decline
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="spinner"></div>
                    <span className="btn-text">Creating Account...</span>
                  </>
                ) : (
                  <>
                    <span className="icon"><FiCheck /></span>
                    <span className="btn-text">Accept & Create Account</span>
                  </>
                )}
              </button>
            </div>
          </form>
          <div className="auth-footer" style={{marginTop: '1.5rem'}}>
            By accepting this invitation, you agree to join the MailMaster team and will have access to the platform based on your assigned role.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation; 